// client/src/components/ticketing/EventAttendanceAnalytics.jsx
//
// Attendance / revenue / attribution dashboard for one event, reading the
// three endpoints added in Phase 7 (server/routes/ticketingRoutes.js).
// Uses recharts (already a dependency, same BarChart/ResponsiveContainer
// combo as components/analytics/PostAnalytics.jsx) for the attendance
// chart; revenue and attribution are simple tables.

import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { formatCurrency } from '../../utils/currencyUtils';

export default function EventAttendanceAnalytics({ tenantId, eventId }) {
  const base = `/api/ticketing/${tenantId}/event/${eventId}/analytics`;

  const [attendance, setAttendance] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [attribution, setAttribution] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [attendanceRes, revenueRes, attributionRes] = await Promise.all([
        axios.get(`${base}/attendance`),
        axios.get(`${base}/revenue`),
        axios.get(`${base}/attribution`),
      ]);
      setAttendance(attendanceRes.data);
      setRevenue(revenueRes.data);
      setAttribution(attributionRes.data);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [base]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return <div className="p-6 text-sm opacity-70">Loading analytics…</div>;
  }
  if (error) {
    return <div className="p-6 text-sm text-red-600">{error}</div>;
  }

  const chartData = (attendance?.byTier || []).map((t) => ({
    name: t.tier_name,
    'Checked in': t.checked_in,
    'Not checked in': t.not_checked_in,
    Cancelled: t.cancelled,
  }));

  return (
    <div className="max-w-3xl mx-auto p-4 space-y-8">
      <h2 className="text-xl font-semibold">Event Analytics</h2>

      <section>
        <h3 className="font-semibold mb-2">Attendance</h3>
        <div className="flex gap-4 mb-3 text-sm">
          <Stat label="Total tickets" value={attendance?.totals?.total ?? 0} />
          <Stat label="Checked in" value={attendance?.totals?.checked_in ?? 0} />
          <Stat label="Not checked in" value={attendance?.totals?.not_checked_in ?? 0} />
        </div>
        {chartData.length > 0 && (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.15)" />
              <XAxis dataKey="name" tick={{ fill: '#e5e7eb' }} />
              <YAxis allowDecimals={false} tick={{ fill: '#e5e7eb' }} />
              <Tooltip />
              <Legend wrapperStyle={{ color: '#e5e7eb' }} />
              <Bar dataKey="Checked in" stackId="a" fill="#ffa500" />
              <Bar dataKey="Not checked in" stackId="a" fill="#4a5b8f" />
              <Bar dataKey="Cancelled" stackId="a" fill="#9ca3af" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </section>

      <section>
        <h3 className="font-semibold mb-2">Revenue</h3>
        {/* One stat row per currency — almost always just one, but tiers on
            the same event can be priced in different currencies (Phase 8),
            and blending them into a single "$" figure would be wrong. */}
        {(revenue?.totalsByCurrency || []).map((t) => (
          <div key={t.currency} className="flex gap-4 mb-3 text-sm">
            <Stat label={`Net revenue (${t.currency.toUpperCase()})`} value={formatCurrency(t.net_revenue, t.currency)} />
            <Stat
              label="Ticket fees"
              value={formatCurrency(t.platform_fees, t.currency)}
              hint="Fee on tickets sold beyond your plan's monthly allotment"
            />
            <Stat label="Orders" value={t.order_count} />
          </div>
        ))}
        {(revenue?.totalsByCurrency || []).length === 0 && (
          <div className="flex gap-4 mb-3 text-sm">
            <Stat label="Net revenue" value={formatCurrency(0)} />
            <Stat label="Orders" value={0} />
          </div>
        )}
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left border-b border-white/10 opacity-70">
              <th className="py-1">Tier</th>
              <th className="py-1 text-right">Gross</th>
              <th className="py-1 text-right">Refunded</th>
              <th className="py-1 text-right">Net</th>
              <th className="py-1 text-right">Orders</th>
            </tr>
          </thead>
          <tbody>
            {(revenue?.byTier || []).map((t) => (
              <tr key={t.tier_id} className="border-b border-white/5">
                <td className="py-1">{t.tier_name}</td>
                <td className="py-1 text-right">{formatCurrency(t.gross_revenue, t.currency)}</td>
                <td className="py-1 text-right">{formatCurrency(t.refunded_amount, t.currency)}</td>
                <td className="py-1 text-right">{formatCurrency(t.net_revenue, t.currency)}</td>
                <td className="py-1 text-right">{t.order_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section>
        <h3 className="font-semibold mb-2">Source attribution</h3>
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left border-b border-white/10 opacity-70">
              <th className="py-1">Source</th>
              <th className="py-1">Medium</th>
              <th className="py-1 text-right">Orders</th>
              <th className="py-1 text-right">Revenue</th>
            </tr>
          </thead>
          <tbody>
            {(attribution?.bySource || []).map((s) => (
              <tr key={`${s.source}-${s.medium}`} className="border-b border-white/5">
                <td className="py-1">{s.source}</td>
                <td className="py-1">{s.medium || '—'}</td>
                <td className="py-1 text-right">{s.orders}</td>
                <td className="py-1 text-right">{formatCurrency(s.revenue, s.currency)}</td>
              </tr>
            ))}
            {(attribution?.bySource || []).length === 0 && (
              <tr>
                <td colSpan={4} className="py-3 text-center opacity-60">No completed orders yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function Stat({ label, value, hint }) {
  return (
    <div className="rounded-lg border border-white/10 px-3 py-2" title={hint || undefined}>
      <div className="text-xs opacity-60">{label}</div>
      <div className="font-semibold">{value}</div>
    </div>
  );
}
